import * as THREE from "three";
import { HqMaterials } from "./hqMaterials";

/**
 * The reward chest's own effects (chestStage.ts), on top of the shared VFX library's sparks and rings: the magic sigil
 * on the floor, the glow in the chest's mouth and its light, the flare that leaks out of the lid's seam, the pillar of
 * light that climbs out when it opens, the shockwaves on the floor, and the fountain of 3D coins and gem chips.
 * Everything is in world units of the stage (the chest model at ChestStage.Scale), additive and unlit except the
 * coins, which are lit metal. Presentation only.
 */

const additive = (uniforms: Record<string, THREE.IUniform>, vertexShader: string, fragmentShader: string, side: THREE.Side = THREE.FrontSide): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side });

const UV_VERT = `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

/** A rune circle on the floor under the chest: rings, ticks and glyph dashes turning in opposite directions. */
export function floorSigil(colour: THREE.Color, radius: number): { mesh: THREE.Mesh; level: { value: number } } {
  const level = { value: 0 };
  const mat = additive({ uTime: HqMaterials.beamTime, uLevel: level, uColor: { value: colour } }, UV_VERT, `
    varying vec2 vUv; uniform float uTime, uLevel; uniform vec3 uColor;
    float ring(float r, float at, float w) { return 1.0 - smoothstep(0.0, w, abs(r - at)); }
    void main() {
      vec2 p = vUv * 2.0 - 1.0;
      float r = length(p), a = atan(p.y, p.x);
      float v = ring(r, 0.92, 0.018) + ring(r, 0.84, 0.01) * 0.8 + ring(r, 0.56, 0.012) * 0.9 + ring(r, 0.5, 0.006) * 0.6;
      float a1 = a + uTime * 0.25, a2 = a - uTime * 0.4;
      float ticks = step(0.75, fract(a1 * 48.0 / 6.2832)) * step(0.85, r) * step(r, 0.91);
      float glyph = step(0.55, fract(a2 * 12.0 / 6.2832)) * step(0.6, r) * step(r, 0.8) * (0.5 + 0.5 * sin(a2 * 36.0));
      float star = 0.0;
      for (int i = 0; i < 6; i++) { float ang = float(i) * 1.0472 + uTime * 0.25; vec2 d = vec2(cos(ang), sin(ang)); star += (1.0 - smoothstep(0.0, 0.012, abs(dot(p, vec2(-d.y, d.x))))) * step(r, 0.56); }
      float glow = pow(max(1e-6, 1.0 - r), 2.0) * 0.6;
      float edge = smoothstep(1.0, 0.9, r);
      float f = (v + ticks * 0.8 + glyph * 0.7 + star * 0.35 + glow) * edge * uLevel;
      gl_FragColor = vec4(uColor * f * 0.9, f);
    }`);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(radius * 2, radius * 2), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.006;
  mesh.renderOrder = -1;
  return { mesh, level };
}

/**
 * The pillar of light out of the chest's mouth: an open cylinder whose light streams upward and fades with height,
 * with a hot inner core. `level` drives it (a flare at the burst, settling to a soft column).
 */
export function lightPillar(colour: THREE.Color, radius: number, height: number): { group: THREE.Group; level: { value: number } } {
  const level = { value: 0 };
  const shader = (core: number) => additive({ uTime: HqMaterials.beamTime, uLevel: level, uColor: { value: colour }, uCore: { value: core } }, `
    varying vec2 vUv; varying float vFace;
    void main() {
      vUv = uv;
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      vec3 n = normalMatrix * normal, e = -mv.xyz;
      vFace = abs(dot(n, e)) / max(1e-6, length(n) * length(e));
      gl_Position = projectionMatrix * mv;
    }`, `
    varying vec2 vUv; varying float vFace; uniform float uTime, uLevel, uCore; uniform vec3 uColor;
    float h(float n) { return fract(sin(n) * 43758.5453); }
    float noise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
      return mix(mix(h(i.x + i.y * 57.0), h(i.x + 1.0 + i.y * 57.0), f.x), mix(h(i.x + (i.y + 1.0) * 57.0), h(i.x + 1.0 + (i.y + 1.0) * 57.0), f.x), f.y); }
    void main() {
      float y = vUv.y;
      float streak = noise(vec2(vUv.x * 18.0, y * 3.0 - uTime * 2.6)) * 0.6 + noise(vec2(vUv.x * 40.0, y * 7.0 - uTime * 4.0)) * 0.4;
      float fade = pow(clamp(1.0 - y, 1e-6, 1.0), 1.6) * smoothstep(0.0, 0.04, y);
      float soft = pow(clamp(vFace, 1e-6, 1.0), 1.5);   // never pow(0, y): on Metal (Mac, iPhone) it made NaN, and the chest went black
      float a = (0.35 + 0.65 * streak) * fade * soft * uLevel;
      vec3 col = mix(uColor, vec3(1.0), uCore) * a * 1.5;
      gl_FragColor = vec4(col, a);
    }`, THREE.DoubleSide);
  const group = new THREE.Group();
  const outer = new THREE.Mesh(new THREE.CylinderGeometry(radius * 1.25, radius, height, 32, 1, true), shader(0.1));
  const inner = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.5, radius * 0.35, height * 0.9, 24, 1, true), shader(0.55));
  outer.position.y = height / 2;
  inner.position.y = height * 0.45;
  group.add(outer, inner);
  group.renderOrder = 2;
  return { group, level };
}

/** Expanding rings on the floor (the drop's landing, the burst). Pooled: `spawn` reuses the oldest. */
export class Shockwaves {
  readonly group = new THREE.Group();
  private readonly rings: { mesh: THREE.Mesh; t: number; life: number; size: number; u: { value: number } }[] = [];

  constructor(private readonly colour: THREE.Color, count = 4) {
    for (let i = 0; i < count; i++) {
      const u = { value: 0 };
      const mat = additive({ uT: u, uColor: { value: new THREE.Color() } }, UV_VERT, `
        varying vec2 vUv; uniform float uT; uniform vec3 uColor;
        void main() {
          float r = length(vUv * 2.0 - 1.0);
          float w = mix(0.16, 0.04, uT);
          float ring = (1.0 - smoothstep(0.0, w, abs(r - 0.92 + w * 0.5))) * (1.0 - uT) * (1.0 - uT);
          float fill = (1.0 - smoothstep(0.0, 0.95, r)) * (1.0 - uT) * 0.08 * (1.0 - uT);
          float a = ring + fill;
          gl_FragColor = vec4(uColor * a * 1.8, a);
        }`);
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = 0.01;
      mesh.visible = false;
      this.group.add(mesh);
      this.rings.push({ mesh, t: 1, life: 1, size: 1, u });
    }
  }

  /** A ring growing to `size` (world units across) over `life` s; `white` 0–1 lifts its colour toward white. */
  spawn(size: number, life: number, white = 0): void {
    const r = this.rings.reduce((a, b) => (b.t > a.t ? b : a));
    r.t = 0; r.life = life; r.size = size;
    ((r.mesh.material as THREE.ShaderMaterial).uniforms.uColor.value as THREE.Color).copy(this.colour).lerp(new THREE.Color(1, 1, 1), white);
    r.mesh.visible = true;
  }

  update(dt: number): void {
    for (const r of this.rings) {
      if (r.t >= 1) { r.mesh.visible = false; continue; }
      r.t = Math.min(1, r.t + dt / r.life);
      const e = 1 - (1 - r.t) * (1 - r.t) * (1 - r.t);
      r.mesh.scale.setScalar(0.2 * r.size + 0.8 * r.size * e);
      r.u.value = r.t;
    }
  }
}

/** A camera-facing soft glow: the light leaking out of the lid's seam, and the burst's hot core. */
export function glowSprite(colour: THREE.Color, stretch = 1): { mesh: THREE.Mesh; level: { value: number } } {
  const level = { value: 0 };
  const mat = additive({ uLevel: level, uColor: { value: colour }, uStretch: { value: stretch } }, UV_VERT, `
    varying vec2 vUv; uniform float uLevel, uStretch; uniform vec3 uColor;
    void main() {
      vec2 p = (vUv * 2.0 - 1.0) * vec2(1.0, uStretch);
      float r = length(p);
      float core = pow(max(1e-6, 1.0 - r), 3.0);
      float streak = pow(max(1e-6, 1.0 - abs(p.y) * 6.0), 2.0) * pow(max(1e-6, 1.0 - abs(p.x)), 1.5);
      float a = (core + streak * 0.8) * uLevel;
      gl_FragColor = vec4(mix(uColor, vec3(1.0), core * 0.5) * a * 1.6, a);
    }`);
  mat.depthTest = false;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
  mesh.renderOrder = 3;
  return { mesh, level };
}

/**
 * The glow inside the chest's mouth: a quad at the rim facing up (the closed lid hides it, so light shows only where the
 * lid lifts) and a point light that lights the hoard and the lid's lining.
 */
export function mouthGlow(colour: THREE.Color, width: number, depth: number): { mesh: THREE.Mesh; light: THREE.PointLight; level: { value: number } } {
  const level = { value: 0 };
  const mat = additive({ uLevel: level, uColor: { value: colour } }, UV_VERT, `
    varying vec2 vUv; uniform float uLevel; uniform vec3 uColor;
    void main() {
      vec2 p = vUv * 2.0 - 1.0;
      float f = (1.0 - smoothstep(0.55, 1.0, abs(p.x))) * (1.0 - smoothstep(0.45, 1.0, abs(p.y)));
      float a = f * uLevel;
      gl_FragColor = vec4(mix(uColor, vec3(1.0, 0.95, 0.8), 0.2) * a * 0.9, a);
    }`);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = 1;
  const light = new THREE.PointLight(colour, 0, 3.5, 1.6);
  return { mesh, light, level };
}

/**
 * The fountain: gold coins (lit metal discs) and gem chips thrown out of the mouth, tumbling, bouncing on the floor
 * and shrinking away. Two instanced meshes; `burst` (re)throws them all from `from`.
 */
export class CoinFountain {
  readonly group = new THREE.Group();
  private readonly coins: THREE.InstancedMesh;
  private readonly gems: THREE.InstancedMesh;
  private readonly bodies: { m: THREE.InstancedMesh; i: number; p: THREE.Vector3; v: THREE.Vector3; q: THREE.Quaternion; w: THREE.Vector3; age: number; life: number; s: number; delay: number }[] = [];
  private live = false;
  static readonly Gravity = 7.5;
  static readonly Bounce = 0.38;

  constructor(gemColour: THREE.Color, coinCount: number, gemCount: number, scale: number) {
    const coinGeo = new THREE.CylinderGeometry(0.034 * scale, 0.034 * scale, 0.008 * scale, 16);
    const coinMat = new THREE.MeshStandardMaterial({ color: 0xffc23a, metalness: 1, roughness: 0.28, emissive: 0x4a2a00, envMapIntensity: 1.2 });
    const gemGeo = new THREE.OctahedronGeometry(0.03 * scale, 0);
    const gemMat = new THREE.MeshStandardMaterial({ color: gemColour, metalness: 0.2, roughness: 0.12, emissive: gemColour.clone().multiplyScalar(0.6), flatShading: true });
    this.coins = new THREE.InstancedMesh(coinGeo, coinMat, Math.max(1, coinCount));
    this.gems = new THREE.InstancedMesh(gemGeo, gemMat, Math.max(1, gemCount));
    for (const [m, n] of [[this.coins, coinCount], [this.gems, gemCount]] as const) {
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.frustumCulled = false;
      m.count = n;
      for (let i = 0; i < n; i++) this.bodies.push({ m, i, p: new THREE.Vector3(), v: new THREE.Vector3(), q: new THREE.Quaternion(), w: new THREE.Vector3(), age: 0, life: 0, s: 0, delay: 0 });
    }
    this.group.add(this.coins, this.gems);
    this.group.visible = false;
    this.hideAll();
  }

  /** Throws everything from `from` (the mouth), up and out, with `power` scaling the speed; staggered over ~0.35 s. */
  burst(from: THREE.Vector3, power: number): void {
    this.live = true;
    this.group.visible = true;
    this.bodies.forEach((b, k) => {
      const a = Math.random() * Math.PI * 2, out = (0.6 + Math.random() * 1.2) * power;
      b.p.copy(from).add(new THREE.Vector3((Math.random() - 0.5) * 0.3, 0, (Math.random() - 0.5) * 0.2));
      b.v.set(Math.cos(a) * out, (3.2 + Math.random() * 2.4) * power, Math.sin(a) * out * 0.8 + 0.6 * power);
      b.q.setFromEuler(new THREE.Euler(Math.random() * 6, Math.random() * 6, Math.random() * 6));
      b.w.set((Math.random() - 0.5) * 22, (Math.random() - 0.5) * 22, (Math.random() - 0.5) * 22);
      b.age = 0;
      b.life = 1.9 + Math.random() * 0.9;
      b.delay = (k / this.bodies.length) * 0.35 * Math.random();
      b.s = 1;
    });
  }

  /** A rain of gold from above over `centre` (a legendary's finale): everything falls in, staggered over ~0.8 s. */
  rain(centre: THREE.Vector3, spread: number, height: number): void {
    this.live = true;
    this.group.visible = true;
    this.bodies.forEach((b, k) => {
      b.p.set(centre.x + (Math.random() - 0.5) * spread * 2, centre.y + height * (0.8 + Math.random() * 0.5), centre.z + (Math.random() - 0.5) * spread);
      b.v.set(0, -1 - Math.random(), 0);
      b.q.setFromEuler(new THREE.Euler(Math.random() * 6, Math.random() * 6, Math.random() * 6));
      b.w.set((Math.random() - 0.5) * 16, (Math.random() - 0.5) * 16, (Math.random() - 0.5) * 16);
      b.age = 0;
      b.life = 2.4 + Math.random() * 0.8;
      b.delay = (k / this.bodies.length) * 0.8;
    });
  }

  private readonly _m = new THREE.Matrix4();
  private readonly _dq = new THREE.Quaternion();
  private readonly _e = new THREE.Euler();
  private readonly _s = new THREE.Vector3();

  update(dt: number): void {
    if (!this.live) return;
    let any = false;
    for (const b of this.bodies) {
      if (b.delay > 0) { b.delay -= dt; this.write(b, 0); any = true; continue; }
      b.age += dt;
      if (b.age >= b.life) { this.write(b, 0); continue; }
      any = true;
      b.v.y -= CoinFountain.Gravity * dt;
      b.p.addScaledVector(b.v, dt);
      if (b.p.y < 0.02) {                                         // bounce on the floor, losing speed and spin
        b.p.y = 0.02;
        if (b.v.y < 0) { b.v.y *= -CoinFountain.Bounce; b.v.x *= 0.7; b.v.z *= 0.7; b.w.multiplyScalar(0.6); }
      }
      this._dq.setFromEuler(this._e.set(b.w.x * dt, b.w.y * dt, b.w.z * dt));
      b.q.multiply(this._dq);
      const fade = Math.min(1, (b.life - b.age) / 0.4);
      this.write(b, fade);
    }
    this.coins.instanceMatrix.needsUpdate = true;
    this.gems.instanceMatrix.needsUpdate = true;
    if (!any) { this.live = false; this.group.visible = false; }
  }

  private write(b: (typeof this.bodies)[number], s: number): void {
    this._m.compose(b.p, b.q, this._s.setScalar(Math.max(1e-4, s)));
    b.m.setMatrixAt(b.i, this._m);
  }

  private hideAll(): void {
    for (const b of this.bodies) this.write(b, 0);
    this.coins.instanceMatrix.needsUpdate = true;
    this.gems.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const m of [this.coins, this.gems]) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); m.dispose(); }
  }
}
